/* ──────────────────────────────────────────────────────────────────
   HEA Cart Drawer — vanilla JS controller.

   Replaces Rebuy SmartCart. Owns:
     • Drawer open/close, scroll lock, focus trap, ESC-to-close
     • Universal ATC interception (every form[action*="/cart/add"])
     • /cart.js state mirror, /cart/add.js, /cart/change.js wrappers
     • Section-render re-fetch of drawer body after every mutation
     • Custom events: cart:change, cart:open, cart:close

   Why a delegated submit listener instead of binding each form:
   forms get inserted dynamically (quick-shop modals, future widgets) —
   binding once on init misses them. Delegation on document catches
   every form regardless of when it entered the DOM. This is the fix
   for the Rebuy "ATC sometimes doesn't fire" bug.

   Exposes: window.HEACart with open(), close(), addItem(), change(),
   refresh(), getState().
   ────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.HEACart) return;

  var SECTION_ID = 'api-cart-drawer';

  var HEACart = {
    state: { item_count: 0, total_price: 0, items: [] },
    drawer: null,
    body: null,
    isOpen: false,
    inFlight: false,
    lastFocus: null,
    pendingAdd: null,

    init: function () {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) {
        // Drawer not in DOM (e.g., /cart page where we don't render it).
        // Still bind ATC so adding from PDP redirects to /cart naturally.
        this.bindATC();
        return;
      }
      this.body = this.drawer.querySelector('[data-cart-body]');

      this.bindCloseHandlers();
      this.bindKeyHandlers();
      this.bindBodyDelegation();
      this.bindATC();
      this.bindOpenTriggers();

      // Hydrate state from /cart.js so client mirror is accurate even if
      // the page was served from cache or item count changed in another tab.
      this.hydrate();

      document.body.setAttribute('data-cart-bound', 'true');
    },

    /* ───── State ──────────────────────────────────────────────── */

    hydrate: function () {
      var self = this;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self.setState(cart); return cart; })
        .catch(function () { return null; });
    },

    setState: function (cart) {
      if (!cart) return;
      this.state = cart;
      this.updateCount();
      this.updateSubtotal();
      this.toggleFooter();
      this.syncWallets(cart);
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
    },

    // DEV-447: the express wallet row in the footer is server-rendered once
    // per page. When the page loads with an empty cart, Shopify renders a
    // reduced wallet set (measured 2026-10-02: shop_pay, amazon_pay, paypal;
    // google_pay only appears once the cart has an item, and Apple Pay is
    // the same kind of wallet). So the first add of a session would show an
    // incomplete row until the next page load. On the first change to a
    // non-empty cart, re-fetch the drawer section once and swap in its
    // freshly rendered wallet markup; Shopify's element initialises itself
    // when inserted. The page-load row stays hidden until then (CSS on
    // [data-wallets-empty-cart]) so the footer grows once, not twice; if
    // the fetch fails, that row is revealed instead.
    syncWallets: function (cart) {
      if (!this.drawer || !cart || !(cart.item_count > 0)) return;
      var wrap = this.drawer.querySelector('[data-cart-wallets][data-wallets-empty-cart]');
      if (!wrap || this.walletsLoading) return;
      var self = this;
      this.walletsLoading = true;
      // No Accept: application/json header here: with it Shopify renders the
      // section without the wallet markup (measured on preview 2026-10-02),
      // which left an empty row after the first add.
      fetch('/?sections=mini-cart')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          var html = json && json['mini-cart'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-wallets]');
          if (!fresh || fresh.hasAttribute('data-wallets-empty-cart')) return;
          if (!fresh.querySelector('shopify-accelerated-checkout-cart')) return;
          wrap.innerHTML = fresh.innerHTML;
        })
        .catch(function () { /* fall back to the row rendered at page load */ })
        .then(function () {
          wrap.removeAttribute('data-wallets-empty-cart');
          self.walletsLoading = false;
          self.whenWalletsSettle(wrap, function () { self.revealFooter(); });
        });
    },

    // Shopify draws the wallet buttons a few at a time after insertion.
    // Settled = the row's height has been non-zero and unchanged for three
    // checks 50ms apart (or 2.5s have passed, whichever comes first).
    // In Safari the phone stack first draws at Shopify's default button
    // height (260px for four wallets, measured 2026-10-15) and only then
    // takes our 44px (200px), so a stable height alone is not enough: when
    // the wallet buttons are known, also wait for the stack to reach their
    // count x --cd-wallet-h plus the 8px gaps. Before the buttons exist
    // Shopify shows a 260px placeholder, so a stack with no buttons yet is
    // never settled (the 2.5s cap still applies).
    whenWalletsSettle: function (wrap, done) {
      var last = -1, same = 0, start = Date.now();
      var unit = parseFloat(getComputedStyle(wrap).getPropertyValue('--cd-wallet-h')) || 0;
      var stacked = !wrap.classList.contains('additional-checkout-buttons--horizontal');
      (function tick() {
        var h = wrap.getBoundingClientRect().height;
        same = (h > 0 && h === last) ? same + 1 : 0;
        last = h;
        var n = 0;
        wrap.querySelectorAll('[slot^="button-"]').forEach(function (s) { if (s.getBoundingClientRect().width > 0) n++; });
        var atTarget = true;
        if (stacked && unit) atTarget = n > 0 && Math.abs(h - (n * unit + (n - 1) * 8)) <= 2;
        if ((same >= 3 && atTarget) || Date.now() - start > 2500) return done();
        setTimeout(tick, 50);
      })();
    },

    revealFooter: function () {
      clearTimeout(this.footerSettleTimer);
      this.footerSettled = true;
      var footer = this.drawer && this.drawer.querySelector('.cart-drawer__footer');
      if (footer) footer.classList.remove('is-settling');
    },

    getState: function () { return this.state; },

    updateCount: function () {
      var count = (this.state && this.state.item_count) || 0;
      var els = document.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < els.length; i++) els[i].textContent = count;
      // Header hooks — keep existing class hooks so header markup/CSS still work.
      var dot = document.querySelector('.Header__CartDot');
      if (dot) dot.classList.toggle('is-visible', count > 0);
      var headerCounts = document.querySelectorAll('.Header__CartCount');
      for (var j = 0; j < headerCounts.length; j++) headerCounts[j].textContent = count;
    },

    updateSubtotal: function () {
      // The footer's SECURE CHECKOUT amount lives outside the re-rendered
      // body, so we update its text directly from /cart.js state.
      var els = document.querySelectorAll('[data-cart-subtotal]');
      var formatted = this.formatMoney(this.state.total_price || 0);
      for (var i = 0; i < els.length; i++) els[i].textContent = formatted;

      // DEV-345: the footer's Subtotal / saving rows live outside the
      // re-rendered body too. Subtotal is the pre-discount figure, the
      // saving is cart.total_discount, the label is every discount
      // application in play (cart-level plus the line-level automatic
      // sale), deduplicated in order of appearance.
      var summary = document.querySelector('[data-cart-summary]');
      if (summary) {
        var st = this.state || {};
        var saving = st.total_discount || 0;
        summary.hidden = !(saving > 0);
        var orig = summary.querySelector('[data-cart-original-total]');
        var sav = summary.querySelector('[data-cart-saving]');
        var lab = summary.querySelector('[data-cart-saving-label]');
        if (orig) orig.textContent = this.formatMoney(st.original_total_price || 0);
        if (sav) sav.textContent = '\u2212' + this.formatMoney(saving);
        if (lab) {
          var titles = [];
          var push = function (t) { if (t && titles.indexOf(t) === -1) titles.push(t); };
          (st.cart_level_discount_applications || []).forEach(function (a) { push(a.title); });
          (st.items || []).forEach(function (it) {
            (it.line_level_discount_allocations || []).forEach(function (a) {
              push(a.discount_application && a.discount_application.title);
            });
          });
          lab.textContent = titles.length ? titles.join(', ') : 'Discount';
        }
      }
    },

    toggleFooter: function () {
      // Hide footer when cart is empty (no checkout button needed).
      if (!this.drawer) return;
      var footer = this.drawer.querySelector('.cart-drawer__footer');
      if (!footer) return;
      var show = !!(this.state && this.state.item_count > 0);
      // DEV-447: first add from an empty-cart page on a phone. The wallet
      // stack is about to be swapped in and drawn (syncWallets), and a
      // footer shown now would put SECURE CHECKOUT on screen and then push
      // it up about 200px as the wallets appear. Lay the footer out but
      // keep it invisible until they settle, never longer than 3 seconds.
      // The desktop row reserves its height (cart-drawer.css), so no hold.
      if (show && footer.hidden && !this.footerSettled) {
        var pending = this.drawer.querySelector('[data-cart-wallets][data-wallets-empty-cart]');
        if (pending && !pending.classList.contains('additional-checkout-buttons--horizontal')) {
          var self = this;
          footer.classList.add('is-settling');
          clearTimeout(this.footerSettleTimer);
          this.footerSettleTimer = setTimeout(function () { self.revealFooter(); }, 3000);
        }
      }
      footer.hidden = !show;
    },

    formatMoney: function (cents) {
      // Shopify exposes Shopify.formatMoney via option_selection.js on most
      // themes. Fall back to a formatter that follows the shop money format.
      var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, fmt);
      }
      // DEV-462 (code review N4): the old fallback printed "$2135.00" beside
      // Liquid's "$2,135.00". Group thousands the way the money filter does.
      var value = (parseInt(cents, 10) || 0) / 100;
      var grouped = function (digits) {
        return value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
      };
      var noDecimals = grouped(0);
      var withDecimals = grouped(2);
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/,/g, '.'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals.replace(/,/g, ' ').replace('.', ',').replace(/ /g, '.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
    },

    /* ───── Open / close ───────────────────────────────────────── */

    open: function () {
      if (!this.drawer || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-visible');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      document.body.classList.add('cart-drawer-open');
      // Focus the close button so screen readers announce + Esc works immediately.
      var firstFocus = this.drawer.querySelector('[data-cart-close]');
      if (firstFocus) firstFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { cart: this.state } }));
    },

    close: function () {
      if (!this.drawer || !this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-visible');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
      document.body.classList.remove('cart-drawer-open');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        try { this.lastFocus.focus({ preventScroll: true }); } catch (e) {}
      }
      document.dispatchEvent(new CustomEvent('cart:close'));
    },

    bindOpenTriggers: function () {
      // Intercept anything that wants to open the cart:
      //   • Prestige header cart icons: [data-action="open-drawer"][data-drawer-id="sidebar-cart"]
      //   • Anything explicitly tagged: [data-cart-open]
      // Plain <a href="/cart"> links are NOT intercepted — those should
      // navigate to the cart page (footer links, etc.) unless they also
      // carry one of the markers above.
      var self = this;
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest(
          '[data-cart-open],' +
          '[data-action="open-drawer"][data-drawer-id="sidebar-cart"]'
        );
        if (!trigger) return;
        // Don't hijack modifier-clicks (open-in-new-tab etc.)
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        self.open();
      }, false);
    },

    bindCloseHandlers: function () {
      var self = this;
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          self.close();
        }
      });
    },

    bindKeyHandlers: function () {
      var self = this;
      document.addEventListener('keydown', function (e) {
        if (!self.isOpen) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          self.close();
        } else if (e.key === 'Tab') {
          self.trapFocus(e);
        }
      });
    },

    trapFocus: function (e) {
      var focusables = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    /* ───── Body delegation (qty + remove) ─────────────────────── */

    bindBodyDelegation: function () {
      var self = this;
      this.drawer.addEventListener('click', function (e) {
        var dec  = e.target.closest('[data-cart-decrease]');
        var inc  = e.target.closest('[data-cart-increase]');
        var rem  = e.target.closest('[data-cart-remove]');

        if (dec) {
          e.preventDefault();
          var line = parseInt(dec.dataset.line, 10);
          var qty  = parseInt(dec.dataset.quantity, 10);
          if (!isNaN(line) && !isNaN(qty)) self.change(line, Math.max(0, qty));
        } else if (inc) {
          e.preventDefault();
          var line2 = parseInt(inc.dataset.line, 10);
          var qty2  = parseInt(inc.dataset.quantity, 10);
          if (!isNaN(line2) && !isNaN(qty2)) self.change(line2, qty2);
        } else if (rem) {
          e.preventDefault();
          var line3 = parseInt(rem.dataset.line, 10);
          if (!isNaN(line3)) self.change(line3, 0);
        } else {
          var ftbAdd = e.target.closest('[data-ftb-add]');
          if (ftbAdd) {
            e.preventDefault();
            self.addFinishBed(ftbAdd);
          }
        }
      });

      // DEV-554: Finish the Bed picker. Keep the row's price, compare-at
      // price and photo on the variant chosen. Bound once on the drawer,
      // which outlives every body re-render, so nothing double-binds.
      this.drawer.addEventListener('change', function (e) {
        var select = e.target.closest && e.target.closest('[data-ftb-select]');
        if (!select) return;
        var row = select.closest('[data-ftb-row]');
        var opt = select.options[select.selectedIndex];
        if (!row || !opt) return;
        var price = row.querySelector('[data-ftb-price]');
        var compare = row.querySelector('[data-ftb-compare]');
        var img = row.querySelector('[data-ftb-image]');
        var onSale = !!opt.dataset.compare;
        if (price) {
          price.textContent = opt.dataset.price || price.textContent;
          price.classList.toggle('hea-ftb__price--sale', onSale);
        }
        if (compare) {
          compare.textContent = opt.dataset.compare || '';
          compare.hidden = !onSale;
        }
        if (img && opt.dataset.image) {
          img.src = opt.dataset.image;
          img.srcset = opt.dataset.image + ' 1x, ' + (opt.dataset.image2x || opt.dataset.image) + ' 2x';
        }
      });
    },

    // DEV-554: "+ Add" in Finish the Bed (snippets/cart-finish-bed-row.liquid).
    // Goes through addItem, the drawer's own add path: /cart/add.js, then
    // refresh() re-renders the body (the added piece leaves the list) and
    // setState updates the count, subtotal and footer. The button is marked
    // .is-loading while the request runs, the same state the product page's
    // Add to Cart takes; it is replaced by the re-render on success and put
    // back on failure (addItem shows the drawer's error toast).
    addFinishBed: function (btn) {
      var self = this;
      if (this.ftbBusy || btn.disabled) return;
      var row = btn.closest('[data-ftb-row]');
      var select = row && row.querySelector('[data-ftb-select]');
      var id = parseInt(select ? select.value : btn.dataset.variantId, 10);
      var qty = parseInt(btn.dataset.quantity, 10) || 1;
      if (!id) return;
      var label = btn.querySelector('.hea-ftb__add-label');
      var rest = label ? label.textContent : null;
      this.ftbBusy = true;
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding';
      this.addItem(id, qty)
        .then(function () {
          // The focused button left with the old body; land on the new line.
          var first = self.body && self.body.querySelector('.cart-drawer__line[data-line="1"] .cart-drawer__line-title a');
          if (first) { try { first.focus({ preventScroll: true }); } catch (err) {} }
        })
        .catch(function () {
          if (document.body.contains(btn)) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
            if (label && rest != null) label.textContent = rest;
          }
        })
        .then(function () { self.ftbBusy = false; });
    },

    /* ───── ATC interception (theme-wide) ──────────────────────── */

    bindATC: function () {
      var self = this;
      // Submit-level interception covers every <form action="*/cart/add*">
      // regardless of when/where it was inserted into the DOM.
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;

        // Honor explicit return_to: user is intentionally going to /cart.
        var returnTo = form.querySelector('[name="return_to"]');
        if (returnTo && returnTo.value) return;

        e.preventDefault();
        self.handleATCForm(form);
      }, false);
    },

    handleATCForm: function (form) {
      var self = this;
      var formData = new FormData(form);
      var submitBtn = form.querySelector('[type="submit"], [name="add"]');
      var origLabel = submitBtn ? submitBtn.innerHTML : null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (j) {
              throw new Error((j && (j.description || j.message)) || 'Could not add to cart.');
            });
          }
          return r.json();
        })
        .then(function () { return self.refresh(); })
        .then(function () { self.open(); })
        .catch(function (err) {
          console.error('[cart] ATC failed:', err);
          self.showError(err && err.message ? err.message : 'Could not add to cart.');
        })
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            if (origLabel != null) submitBtn.innerHTML = origLabel;
          }
        });
    },

    /* ───── Cart mutations ─────────────────────────────────────── */

    addItem: function (variantId, quantity, properties) {
      var self = this;
      quantity = quantity || 1;
      var payload = { items: [{ id: variantId, quantity: quantity }] };
      if (properties) payload.items[0].properties = properties;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (j) {
              throw new Error((j && (j.description || j.message)) || 'Could not add to cart.');
            });
          }
          return r.json();
        })
        .then(function () { return self.refresh(); })
        .then(function () { self.open(); })
        .catch(function (err) {
          console.error('[cart] addItem failed:', err);
          self.showError(err && err.message ? err.message : 'Could not add to cart.');
          throw err;
        });
    },

    change: function (line, quantity) {
      var self = this;
      if (this.inFlight) return Promise.resolve();
      this.inFlight = true;

      // Optimistic visual: dim the affected row while in flight.
      var row = this.body && this.body.querySelector('[data-line="' + line + '"]');
      if (row) {
        var rowEl = row.closest('[data-cart-line]');
        if (rowEl) rowEl.classList.add('is-loading');
      }

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) {
          if (r.ok) return r.json();
          // DEV-462 (code review 06): Shopify answers a change past stock with
          // 422 and a message ("You can only add 7 of ... to the cart."). Say
          // so instead of treating it like success; the refresh below still
          // runs so the row shows the quantity the cart really holds.
          return r.json().catch(function () { return {}; }).then(function (j) {
            self.showError((j && (j.description || j.message)) || 'Could not update cart.');
          });
        })
        .then(function () { return self.refresh(); })
        .catch(function (err) {
          console.error('[cart] change failed:', err);
          self.showError('Could not update cart.');
        })
        .then(function () { self.inFlight = false; });
    },

    refresh: function () {
      var self = this;

      // On the dedicated /cart page there is no drawer: the page's item list
      // and Order Summary are server-rendered by sections/main-cart.liquid,
      // and Prestige's CartSection only re-renders them on its OWN mutation
      // paths (qty steppers / remove links). Mutations that go through
      // HEACart — e.g. ADD from the "Customers Also Loved" band — left the
      // whole page stale (tracker #50). Re-fetch the cart section and swap
      // the same nodes theme.js swaps (.Cart form + free-shipping bar),
      // keeping the section wrapper (and its scoped <style> / delegated
      // listeners) intact.
      var cartSection = document.querySelector('[data-section-type="cart"]');
      var needsPageRender = cartSection && cartSection.querySelector('.Cart');

      return Promise.all([
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }),
        this.body
          ? fetch('/?section_id=' + SECTION_ID, { headers: { Accept: 'text/html' } }).then(function (r) { return r.text(); })
          : Promise.resolve(null),
        needsPageRender
          ? fetch('/cart?section_id=main-cart', { headers: { Accept: 'text/html' } }).then(function (r) { return r.text(); })
          : Promise.resolve(null)
      ]).then(function (results) {
        var cart = results[0];
        var html = results[1];
        var pageHtml = results[2];

        if (pageHtml && cartSection) {
          var pageDoc = new DOMParser().parseFromString(pageHtml, 'text/html');
          var freshCart = pageDoc.querySelector('.Cart');
          var staleCart = cartSection.querySelector('.Cart');
          if (freshCart && staleCart) staleCart.parentNode.replaceChild(freshCart, staleCart);
          // DEV-324: the free-shipping bar and the promo ladder both self-hide
          // in Liquid (empty cart, no eligible line), so a mutation can create
          // or remove either node, not only change it. Swap when both exist,
          // drop a stale node the fresh render no longer has, and insert a
          // fresh node the page never had, in main-cart.liquid's order:
          // shipping bar, ladder, then the .Cart form.
          var syncNode = function (selector, anchorSelector) {
            var fresh = pageDoc.querySelector(selector);
            var stale = cartSection.querySelector(selector);
            if (fresh && stale) { stale.parentNode.replaceChild(fresh, stale); return; }
            if (!fresh && stale) { stale.parentNode.removeChild(stale); return; }
            if (fresh && !stale) {
              var anchor = cartSection.querySelector(anchorSelector);
              if (anchor) anchor.parentNode.insertBefore(fresh, anchor);
            }
          };
          // DEV-345: the shipping bar now renders inside the promo card when
          // the ladder exists and standalone when it does not, so the two are
          // synced as one wrapper node ([data-cart-promo-area], always present)
          // instead of separately; syncing them separately could drop the bar
          // when a stale card that contained it was removed.
          syncNode('[data-cart-promo-area]', '.Cart');
        }

        // DEV-360: swap the drawer body BEFORE setState. setState dispatches
        // cart:change synchronously and listeners (the Customers Also Loved
        // band's cached-replay path) paint into the current DOM; announcing
        // state first let them paint into nodes this innerHTML swap was about
        // to destroy, so recommendations vanished after same-seed mutations
        // (quantity changes, removing a non-seed line).
        if (self.body && html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          // The section endpoint renders body innerHTML directly; pull it.
          var section = doc.querySelector('.shopify-section') || doc.body;
          if (section) self.body.innerHTML = section.innerHTML;
        }
        self.setState(cart);
      });
    },

    /* ───── Error surfacing ────────────────────────────────────── */

    showError: function (msg) {
      // Lightweight inline toast inside the drawer. Falls back to alert if
      // the drawer isn't present (e.g., adding from /cart page).
      if (!this.drawer) {
        try { window.alert(msg); } catch (e) {}
        return;
      }
      // DEV-462 (code review N3): a refused add ("The maximum quantity of this
      // item is already in your cart") used to be written into the closed
      // drawer, so the shopper saw the button spin and nothing else. Open the
      // drawer first so the message, and the cart it is about, are on screen.
      if (!this.isOpen) this.open();
      var toast = this.drawer.querySelector('[data-cart-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-drawer__toast';
        toast.setAttribute('data-cart-toast', '');
        toast.setAttribute('role', 'alert');
        this.drawer.querySelector('.cart-drawer__panel').appendChild(toast);
      }
      toast.textContent = msg;
      toast.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () {
        toast.classList.remove('is-visible');
      }, 3500);
    }
  };

  window.HEACart = HEACart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { HEACart.init(); });
  } else {
    HEACart.init();
  }
})();
